import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from "@ngrx/effects";

import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { DiscountTypes } from "graphql.generated";


import { cloneDeep } from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';
import { EMPTY, from, merge, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, tap } from 'rxjs/operators';

import { jobWorkordersFeature } from 'src/app/job/job-workorders/job-workorders.feature';
import { selectAddChargesDialogPresetCharges } from 'src/app/job/job-workorders/job-workorders.selectors';

import { JobMutateChargeActions } from 'src/app/job/mutate-charges/mutate-charges.actions';

import { CreateInvoiceGQL } from '../../../generated/graphql.generated';
import { formatCurrency } from '../../lib.ts/currency.util';
import { DetailsHelperService } from '../../services/details-helper.service';
import { DocumentHelperService } from '../../services/document-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { ResponsiveHelperService } from '../../services/responsive-helper.service';
import { selectJobSaveChanges } from '../../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { ApplyTransactionComponent } from '../../transactions/apply-transaction/apply-transaction.component';

import { generateUUID } from '../../utilities/state.util';

import { JobInvoiceActions } from '../job-invoice/job-invoice.actions';
import { JobToolActions } from '../job-tool.actions';
import { WorkOrdersActions } from '../job-workorders/job-workorders.actions';
import { EditableCharge, EditableDiscount } from '../job-workorders/job-workorders.state';

import { JobMutateObjectActions } from './mutate-object.actions';
import { mutateObjectFeature } from './mutate-object.feature';

export const addCustomCharge = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(JobMutateObjectActions.mutateButtonClicked),
      concatLatestFrom(() => store.select(createSelector(
        mutateObjectFeature.selectMutateObjectDialog,
        jobWorkordersFeature.selectAddChargesDialog,
        selectAddChargesDialogPresetCharges,
        (mutateObjectDialog, addChargesDialog, presetCharges) => ({
          mutateObjectDialog,
          addChargesDialog,
          presetCharges,
        }),
      ))),
      filter(([_, { mutateObjectDialog }]) => mutateObjectDialog?.activeMutator === 'charge'),
      filter(([_, { mutateObjectDialog }]) => mutateObjectDialog?.mutateType === 'create'),

      exhaustMap(([_, { mutateObjectDialog, addChargesDialog, presetCharges }]) => {
        const {
          eventIds,
          amount,
          quantity,
          productId,
          productName,
          description,
          expenses,
          taxes,
        } = mutateObjectDialog.mutators.charge;

        // Emit one chargesAdded action per event to avoid issue with
        // custom charges having same id when added to multiple events
        return from(eventIds ?? []).pipe(
          map((eventId) => {
            const customCharge: EditableCharge = {
              chargeId: generateUUID(),
              amount,
              quantity,
              productId,
              productName,
              description,
              expenses: cloneDeep(expenses ?? []),
              new: true,
              modified: false,
              removed: false,
              taxes: (taxes ?? []).map(t => ({
                id: t.id,
                percentage: t.percentage,
              })),
            };

            const isPresetTarget = eventId === addChargesDialog.selectedEventId;
            const charges = isPresetTarget ? [...presetCharges, customCharge] : [customCharge];

            return WorkOrdersActions.chargesAdded({
              eventIds: [eventId],
              charges,
            });
          }),
          catchError((error) =>
            of(JobMutateObjectActions.mutateButtonClickedError({ error }))
          )
        );
      }),
    );
  },
  { functional: true, dispatch: true }
);

export const addCustomDiscount = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobMutateObjectActions.mutateButtonClicked),
      concatLatestFrom(() => store.select(mutateObjectFeature.selectMutateObjectDialog)),
      filter(([ _action, { activeMutator } ]) => activeMutator === 'discount'),
      map(([ _action, mutateObjectDialog ]): {
        discount: EditableDiscount;
        eventIds: string[];
      } => {

        const discountType = mutateObjectDialog.mutators.discount.discountType;

        let customAmount = mutateObjectDialog.mutators.discount.amount;

        if (discountType === DiscountTypes.Amount) {
          customAmount = customAmount * 100;
        }

        return {
          discount: {
            appliedId: generateUUID(),
            name: mutateObjectDialog.mutators.discount.discountName,
            code: mutateObjectDialog.mutators.discount.code,
            customAmount,
            discountType,
            new: true,
            removed: false,
          },
          eventIds: mutateObjectDialog.mutators.discount.eventIds,
        }
      }),
      map((payload) => WorkOrdersActions.discountAdded(payload)),
    );
}, { functional: true, dispatch: true });

export const loadCharge = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
      return actions$.pipe(
        ofType(WorkOrdersActions.editChargeButtonClicked),
        concatLatestFrom(({ chargeId, eventId }) => store.select(createSelector(
          jobWorkordersFeature.selectWorkordersEditor,
          (editor) => {

            const event = editor?.workorders?.find((wo) => wo.eventId === eventId);

            if (!event) return;

            const charge = event.charges?.find((c) => c.chargeId === chargeId);

            return charge;
          }
        ))),
        filter(([ _action, charge ]) => Boolean(charge)),
        map(([ { eventId }, charge ]) => JobMutateChargeActions.chargeLoaded({ charge, eventId })),
      );
    }, { functional: true, dispatch: true });

export const editCharge = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(JobMutateObjectActions.mutateButtonClicked),
      concatLatestFrom(() => store.select(mutateObjectFeature.selectMutateObjectDialog)),
      filter(([_, dialog]) => dialog?.activeMutator === 'charge'),
      filter(([_, dialog]) => dialog?.mutateType === 'update'),
      exhaustMap(([_, mutateObjectDialog]) => {
        const {
          chargeId,
          description,
          eventIds,
        } = mutateObjectDialog.mutators.charge;

        const eventId = eventIds?.[0];
        if (!eventId) {
          return of(JobMutateObjectActions.mutateButtonClickedError({
            error: new Error('No event ID available for charge update')
          }));
        }

        return of(
          WorkOrdersActions.chargeDescriptionChanged({
            chargeId,
            description,
            eventId,
          })
        );
      })
    );
  },
  { functional: true, dispatch: true }
);

export const createInvoice = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  createInvoiceGQL = inject(CreateInvoiceGQL),
) => {

    const createInvoiceClickedNoChanges$ = actions$.pipe(
      ofType(JobInvoiceActions.createInvoiceButtonClicked),
      // Pending Crew Pay boxes on a shared job are left for the banner: they never reach the
      // full save, so waiting on it would never fire, and the document does not show crew pay.
      concatLatestFrom(() => store.select(selectJobSaveChanges)),
      filter(([ _action, changes ]) => changes.length === 0),
      map(([ action ]) => ({ eventIds: action.eventIds, jobId: action.jobId })),
    );

    const createInvoiceClickedChangesSaved$ = actions$.pipe(
      ofType(JobToolActions.saveChangesSuccess),
      filter((action) => action.promptedBy.type === JobInvoiceActions.createInvoiceButtonClicked.type),
      map((action) => {
        const promptedBy = action.promptedBy as ReturnType<typeof JobInvoiceActions.createInvoiceButtonClicked>;
        return { eventIds: promptedBy.eventIds, jobId: promptedBy.jobId };
      }),
    );

    const triggers$ = merge(
      createInvoiceClickedNoChanges$,
      createInvoiceClickedChangesSaved$,
    );

    return triggers$.pipe(
      exhaustMap(({ eventIds }) => createInvoiceGQL.mutate({ invoices: [ { eventIds } ]}).pipe(
        map((res) => {

          if (!res?.data?.createInvoice?.invoices?.[0]) {
            return JobInvoiceActions.createInvoiceError({ error: new Error('No invoice in response') });
          }

          const { transactions: appliedTransactions, ...invoice } = res.data.createInvoice.invoices[0];

          return JobInvoiceActions.createInvoiceSuccess({
            invoice,
            eventIds,
            appliedTransactions: appliedTransactions ?? [],
          });
        }),
        catchError((error) => of(JobInvoiceActions.createInvoiceError({ error }))),
      )),
    );

}, { functional: true, dispatch: true });

export const openApplyTransactionsDialog = createEffect((
  actions$ = inject(Actions),
  dialogService = inject(DialogService),
  responsiveHelper = inject(ResponsiveHelperService),
  freyaHelper = inject(FreyaHelperService),
  documentHelper = inject(DocumentHelperService),
  localNotify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.createInvoiceSuccess),
      tap(({ invoice, appliedTransactions }) => {
        if (!appliedTransactions.length) { return; }

        const appliedTotal = appliedTransactions.reduce((acc, t) => acc + t.amount, 0);
        const amount = formatCurrency(appliedTotal / 100, invoice.currency);
        const refunds = appliedTransactions.filter((t) => t.type === 'refund').length;
        const payments = appliedTransactions.length - refunds;
        const counted = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
        const parts = [
          payments && counted(payments, 'existing payment'),
          refunds && counted(refunds, 'refund'),
        ].filter(Boolean).join(' and ');

        localNotify.success(`Applied ${parts} (${amount}) to the invoice`);
      }),
      exhaustMap(({ invoice }) => from(documentHelper.getTransactionsAvailableForInvoice(invoice)).pipe(
        filter(({ transactionsAvailable }) => transactionsAvailable),
        tap(({ transactionsWithAvailability }) => dialogService.open(ApplyTransactionComponent, {
          header: 'Apply Existing Transaction to Invoice?',
          width: responsiveHelper.dialogWidth,
          data: { transactionsWithAvailability, invoice },
          contentStyle: freyaHelper.getDialogContentStyle('1.5rem'),
        })),
        catchError(() => EMPTY),
      )),
    );
}, { functional: true, dispatch: false });

export const openInvoiceInRightPanel = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.createInvoiceSuccess),
      tap(({ invoice }) => {
        detailsHelper.detailsItem.next({ type: 'invoice', item: { id: invoice.id } });
      }),
    );
}, { functional: true, dispatch: false });
